import { useQuery } from '@tanstack/react-query';

import { listRunActivity } from '@/lib/api/generated/api';
import type { RunActivityList } from '@/lib/api/generated/model';
import { taskKeys } from '@/features/tasks/queryKeys';

import type { ActivityEntry } from '../activityFeed';

/**
 * A run's subtask changes, newest first (spec 148). The whole list: a run
 * carries tens of changes, so the service does not page it.
 *
 * Same hand-written-`queryFn` convention as `useTaskMoves`. `entries` is
 * always an array, so a failed read leaves the page's progress events
 * showing rather than taking the panel down.
 */
export function useRunActivity(workspaceId: string, taskId: string) {
  const query = useQuery({
    queryKey: taskKeys.activity(workspaceId, taskId),
    queryFn: async (): Promise<RunActivityList['items']> => {
      const response = await listRunActivity(workspaceId, taskId);
      if (response.status !== 200) {
        throw new Error('The run’s activity could not be loaded.');
      }
      return response.data.items;
    },
    retry: false,
  });

  return {
    entries: (query.data ?? []) as ActivityEntry[],
    isPending: query.isPending,
    loadFailed: query.isError,
    retry: () => {
      void query.refetch();
    },
  };
}
